import { Avatar, Link, Typography } from "@mui/material";
import { getImagePath } from "assets/images";
import { IMenuAccessRightModel } from "models/MenuAccessRightModel";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { RootState } from "redux/store";
import { AppRoutings } from "utility/enums/app-routings";
import { ChangeOrderRequestActionType } from "utility/enums/change-order-request";
import { ContractActionType } from "utility/enums/contract-detail";
import {
	ReqConversionToBidActionType,
	RequisitionActionType,
} from "utility/enums/requisition-status";
import { UserType } from "utility/enums/user-type";
import { Shared } from "utility/shared";

const externalMenuUserRights = [
	{
		menuId: 1,
		menuName: "supply-chain-management-portal",
		menuDescription: "Vendor Portal",
		parentMenuId: null,
		url: "/supply-chain-management-portal",
		icons: [
			"vendor-portal-default-icon.svg",
			"vendor-portal-primary-icon.svg",
			"vendor-portal-dark-grey-icon.svg",
		],
		isVisibleMenu: true,
		displayOrder: 1,
		accessRights: [
			{
				accessRightDescription: "View",
				accessRightId: 1,
				accessRightName: "view",
				isDependOnDepartment: false,
				isRights: true,
				menuAccessRightId: 1,
			},
		],
	},
];

const SidebarMenu: React.FC = () => {
	let navigate = useNavigate();
	const location = useLocation();
	const userMenuAccessRights = useSelector(
		(state: RootState) => state.authState.userMenuAccessRights
	); // fetch menu list from redux based on logged in user rights
	const userDetails = useSelector(
		(state: RootState) => state.authState.userDetails
	); // fetch menu list from redux based on logged in user rights
	const [expandedMenuItem, setExpandedMenuItem] = useState<string>(""); // To set the menu name for expanded options
	const [expandedSubMenuItem, setExpandedSubMenuItem] = useState<string>("");
	const [parentMenuItemList, setParentMenuItemList] = useState<
		IMenuAccessRightModel[]
	>([]); // List for main menu sorted by display order
	const [subMenuItemList, setSubMenuItemList] = useState<
		IMenuAccessRightModel[]
	>([]); // List for sub menu sorted by display order

	useEffect(() => {
		// set the values of main menu and sub menu from menu list
		let sortedList = [];
		if (
			userDetails.userTypeId === UserType.Internal &&
			userMenuAccessRights.length > 0
		) {
			sortedList = Shared.sortData(userMenuAccessRights, "displayOrder"); // Sort list by display order
		}
		if (
			userDetails.userTypeId === UserType.External &&
			externalMenuUserRights.length > 0
		) {
			sortedList = Shared.sortData(externalMenuUserRights, "displayOrder");
		}
		setParentMenuItemList(
			sortedList.filter((i: IMenuAccessRightModel) => i.parentMenuId === null)
		);
		setSubMenuItemList(
			sortedList.filter((i: IMenuAccessRightModel) => i.parentMenuId !== null)
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userMenuAccessRights, userDetails.userTypeId]);

	// get active menu name that is selected
	const getActiveRouteName = useMemo(() => {
		let currentPath = location.pathname;
		if (
			currentPath === AppRoutings.WorkflowDefinition ||
			currentPath === AppRoutings.WorkflowStepDefinition
		) {
			currentPath = AppRoutings.WorkflowSummaryGrid;
		}
		if (currentPath === AppRoutings.InternalUserDetail) {
			currentPath = AppRoutings.InternalUsers;
		}
		if (currentPath === AppRoutings.DepartmentDetail) {
			currentPath = AppRoutings.DepartmentSummaryGrid;
		}
		if (currentPath === AppRoutings.ItemDetail) {
			currentPath = AppRoutings.ItemSummaryGrid;
		}
		if (currentPath === AppRoutings.ProgrammeDetail) {
			currentPath = AppRoutings.ProgrammeSummaryGrid;
		}
		if (
			currentPath === AppRoutings.RequisitionConversionToBid ||
			currentPath.includes(
				`requisition-conversion-to-bid/${ReqConversionToBidActionType.Convert}`
			)
		) {
			currentPath = AppRoutings.RequisitionSummaryGrid;
		}
		if (currentPath === AppRoutings.BidDetail) {
			currentPath = AppRoutings.BidSummaryGrid;
		}
		if (currentPath === AppRoutings.SystemTemplateDefinition) {
			currentPath = AppRoutings.SystemTemplateSummaryGrid;
		}
		if (currentPath === AppRoutings.ProcurementMethodDetail) {
			currentPath = AppRoutings.ProcurementMethodSummaryGrid;
		}
		if (
			currentPath === AppRoutings.RequisitionEntry ||
			currentPath.includes(
				`${AppRoutings.RequisitionEntry}/${RequisitionActionType.Edit}`
			) ||
			currentPath.includes(
				`${AppRoutings.RequisitionEntry}/${RequisitionActionType.Delete}`
			)
		) {
			currentPath = AppRoutings.RequisitionSummaryGrid;
		}
		if (
			currentPath === AppRoutings.NewContractEntry ||
			currentPath === AppRoutings.BidConversionToContract
		) {
			currentPath = AppRoutings.ContractSummaryGrid;
		}
		if (currentPath === AppRoutings.BidConversionToPO) {
			currentPath = AppRoutings.PORequestSummaryGrid;
		}
		if (currentPath === AppRoutings.ErpDbCompanyDetail) {
			currentPath = AppRoutings.ErpDbCompanySummaryGrid;
		}
		if (
			currentPath === AppRoutings.BudgetChangeOrderDetail ||
			currentPath.includes("/budget-change-order-detail/")
		) {
			currentPath = AppRoutings.BudgetChangeOrderSummaryGrid;
		}
		if (
			currentPath === AppRoutings.PerformanceEvaluationDetail ||
			currentPath === AppRoutings.PerformanceEvaluationHistoryDetail
		) {
			currentPath = AppRoutings.PerformanceEvaluationSummaryGrid;
		}
		if (currentPath === AppRoutings.BudgetAllocationDetail) {
			currentPath = AppRoutings.BudgetAllocationSummaryGrid;
		}
		if (currentPath === AppRoutings.GlAccountDetail) {
			currentPath = AppRoutings.GlAccountSummaryGrid;
		}
		if (
			currentPath.includes(
				`${AppRoutings.NewContractEntry}/${ContractActionType.Edit}`
			) ||
			currentPath.includes(
				`${AppRoutings.NewContractEntry}/${ContractActionType.Delete}`
			)
		) {
			currentPath = AppRoutings.ContractSummaryGrid;
		}
		if (
			currentPath === AppRoutings.ContractPOEntry ||
			currentPath.includes(
				`${AppRoutings.ContractPOEntry}/${RequisitionActionType.Edit}`
			) ||
			currentPath.includes(
				`${AppRoutings.ContractPOEntry}/${RequisitionActionType.Delete}`
			)
		) {
			currentPath = AppRoutings.PORequestSummaryGrid;
		}
		if (
			currentPath === AppRoutings.ContractVariationEntry ||
			currentPath.includes(
				`${AppRoutings.ContractVariationEntry}/${RequisitionActionType.Edit}`
			) ||
			currentPath.includes(
				`${AppRoutings.ContractVariationEntry}/${RequisitionActionType.Delete}`
			)
		) {
			currentPath = AppRoutings.VariationSummaryGrid;
		}
		if (
			currentPath === AppRoutings.ChangeOrderRequestEntry ||
			currentPath.includes(
				`${AppRoutings.ChangeOrderRequestEntry}/${ChangeOrderRequestActionType.Edit}`
			) ||
			currentPath.includes(
				`${AppRoutings.ChangeOrderRequestEntry}/${ChangeOrderRequestActionType.Delete}`
			)
		) {
			currentPath = AppRoutings.ChangeOrderRequestSummaryGrid;
		}
		const selectedMenu: IMenuAccessRightModel | undefined =
			parentMenuItemList.find((item) =>
				item.url === currentPath
					? true
					: subMenuItemList
							.filter((i) => i.parentMenuId === item.menuId)
							.findIndex((t) => t.url === currentPath)! > -1
			);
		return selectedMenu ? selectedMenu.menuName : "";
	}, [location.pathname, parentMenuItemList, subMenuItemList]);

	// function to handle on click of main menu item
	const handleParentMenuItemClick = (menuItem: IMenuAccessRightModel) => {
		if (
			subMenuItemList.filter((i) => i.parentMenuId === menuItem.menuId).length
		) {
			setExpandedMenuItem(
				expandedMenuItem !== menuItem.menuName &&
					subMenuItemList.filter((i) => i.parentMenuId === menuItem.menuId)
						.length
					? menuItem.menuName
					: ""
			);
		} else if (menuItem.url) {
			navigate(menuItem.url);
			Shared.hideFlyInPanel();
		}
	};

	// function to handle on click of sub menu item
	const handleSubMenuItemClick = (subMenuItem: IMenuAccessRightModel) => {
		if (subMenuItem.url) {
			navigate(subMenuItem.url);
			Shared.hideFlyInPanel();
		}
	};

	return (
		<div className="sidebar">
			<ul>
				{parentMenuItemList.map(
					(parentMenuItem: IMenuAccessRightModel) =>
						parentMenuItem.isVisibleMenu && (
							<li
								key={parentMenuItem.menuId}
								className={`
										${
											parentMenuItem.parentMenuId === null &&
											subMenuItemList.filter(
												(i) => i.parentMenuId === parentMenuItem.menuId
											).length &&
											"has-menu "
										}
										${parentMenuItem.menuName === expandedMenuItem && "open"}
								`}
							>
								<Link
									href=""
									title={parentMenuItem.menuDescription}
									className={`${
										getActiveRouteName === parentMenuItem.menuName && "active"
									}`}
									underline="none"
									onClick={(
										event:
											| React.MouseEvent<HTMLAnchorElement, MouseEvent>
											| React.MouseEvent<HTMLSpanElement, MouseEvent>
									) => {
										event.preventDefault();
										handleParentMenuItemClick(parentMenuItem);
									}}
								>
									<div className="left-icons">
										{parentMenuItem.icons &&
											parentMenuItem.icons.length > 0 &&
											parentMenuItem.icons.map((icon: string) => (
												<Avatar
													key={icon}
													variant="square"
													src={getImagePath(icon)}
													alt={parentMenuItem.menuDescription}
												/>
											))}
									</div>
									<Typography variant="body1">
										{parentMenuItem.menuDescription}
									</Typography>
								</Link>
								{subMenuItemList.filter(
									(i) => i.parentMenuId === parentMenuItem.menuId
								) && (
									<ul className="submenu-scroll">
										{subMenuItemList
											.filter((i) => i.parentMenuId === parentMenuItem.menuId)
											.map((subMenuItem: IMenuAccessRightModel) => {
												const childSubMenuItems = subMenuItemList.filter(
													(i) => i.parentMenuId === subMenuItem.menuId
												);
												const hasChildren = childSubMenuItems.length > 0;
												const isExpanded =
													expandedSubMenuItem === subMenuItem.menuName;
												return (
													subMenuItem.isVisibleMenu &&
													(hasChildren ? (
														<>
															<li
																key={subMenuItem.menuId}
																className={`submenu-item has-submenu
																	${isExpanded ? "open" : ""}`}
															>
																<Link
																	href=""
																	title={subMenuItem.menuDescription}
																	underline="none"
																	// className={`${
																	// 	subMenuItem.url &&
																	// 	location.pathname === subMenuItem.url &&
																	// 	"active"
																	// }`}
																	className="custom-submenu-link"
																	onClick={(event) => {
																		event.preventDefault();
																		setExpandedSubMenuItem(
																			isExpanded ? "" : subMenuItem.menuName
																		);
																	}}
																>
																	{subMenuItem.menuDescription}
																</Link>
																{isExpanded && childSubMenuItems.length > 0 && (
																	<ul>
																		{childSubMenuItems.map(
																			(childItem: IMenuAccessRightModel) => (
																				<li key={childItem.menuId}>
																					<Link
																						href=""
																						title={childItem.menuDescription}
																						underline="none"
																						className={`${
																							childItem.url &&
																							location.pathname ===
																								childItem.url &&
																							"active"
																						}`}
																						onClick={(
																							event:
																								| React.MouseEvent<
																										HTMLAnchorElement,
																										MouseEvent
																								  >
																								| React.MouseEvent<
																										HTMLSpanElement,
																										MouseEvent
																								  >
																						) => {
																							event.preventDefault();
																							handleSubMenuItemClick(childItem);
																						}}
																					>
																						{childItem.menuDescription}
																					</Link>
																				</li>
																			)
																		)}
																	</ul>
																)}
															</li>
														</>
													) : (
														<li key={subMenuItem.menuId}>
															<Link
																href=""
																title={subMenuItem.menuDescription}
																underline="none"
																className={`${
																	subMenuItem.url &&
																	location.pathname === subMenuItem.url &&
																	"active"
																}`}
																onClick={(
																	event:
																		| React.MouseEvent<
																				HTMLAnchorElement,
																				MouseEvent
																		  >
																		| React.MouseEvent<
																				HTMLSpanElement,
																				MouseEvent
																		  >
																) => {
																	event.preventDefault();
																	handleSubMenuItemClick(subMenuItem);
																}}
															>
																{subMenuItem.menuDescription}
															</Link>
														</li>
													))
												);
											})}
									</ul>
								)}
							</li>
						)
				)}
			</ul>
		</div>
	);
};

export default SidebarMenu;
